import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, ScrollView, StyleSheet, Text, View, useWindowDimensions } from "react-native";
import {
  buildRegionHierarchyRows,
  buildMarketHierarchyRows,
  metricValue,
  MOCK_METRICS,
  MockGrain,
  MockLocationRow,
  MockMetric,
} from "./LocationSummaryV2MockData";
import {
  deriveActiveHierarchyMode,
  buildSearchContext,
  determineAutoExpandKeys,
  filterForVisibility,
} from "./LocationSummaryV2Hierarchy";
import { v2Styles as styles } from "./LocationSummaryV2.styles";
import {
  ALL_GRAINS,
  buildVisibleMetricExportColumns,
  computeColumnWidths,
  getGrainLabel,
  MOBILE_TOOLBAR_BREAKPOINT,
  SortState,
} from "./LocationSummaryV2Table.shared";
import LocationSummaryToolbar from "./LocationSummaryToolbar";
import LocationSummaryColumnsDropdown from "./LocationSummaryColumnsDropdown";
import LocationSummaryTableHeader from "./LocationSummaryTableHeader";
import LocationSummaryTotalRow from "./LocationSummaryTotalRow";
import LocationSummaryTableRow from "./LocationSummaryTableRow";
import LocationSummaryPagination from "./LocationSummaryPagination";
import TableSkeleton from "../../../components/TableSkeleton";
import {
  ASNLocationDetailsRow,
  ASNLocationDetailsGrandTotal,
  ASNLocationDetailsPagination,
  AsnLocationGrainKey,
} from "../api/asnOverviewKpiApi";
import { AsnMetricColumn } from "../constants/asnOnePagerConstants";

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  apiRows: ASNLocationDetailsRow[];
  perspective: "shipping" | "receiving";
  effectiveGrainKeys: AsnLocationGrainKey[];
  grandTotal: ASNLocationDetailsGrandTotal | null;
  serverPagination: ASNLocationDetailsPagination | null;
  loading: boolean;
  error: string | null;
  sortBy: string;
  sortDirection: "asc" | "desc";
  searchInput: string;
  selectedKey?: string;
  onSelect: (row: ASNLocationDetailsRow) => void;
  onSort: (col: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSearchChange: (text: string) => void;
  /** Invoked when the user clicks "Export to Excel"; receives the metric columns
   * and grain columns currently visible in the table (Columns toggle-filtered),
   * in on-screen order — never the full backend aggregation grain. */
  onExport: (visibleMetricColumns: AsnMetricColumn[], visibleGrainKeys: AsnLocationGrainKey[]) => void;
  /** True while an export is in flight — disables the button and shows a spinner. */
  isExporting?: boolean;
}

// ─── Adaptation layer ─────────────────────────────────────────────────────────
// Maps each live ASNLocationDetailsRow (nested API shape) to the flat
// MockLocationRow shape that the rendering sub-components are typed against.
// Field mapping:
//   r.key              → id
//   r.grainVals.*      → grain string fields (region/market/location/year/period/week)
//   r.vals.nonAsnLoads → nonAsnLoads
//   r.vals.totalLoads  → totalLoads
//   r.vals.pctNonAsn   → pctNonAsn  (backend-computed; not recalculated here)
//   r.vals.pyPct       → pyPct      (backend-computed prior-year percentage)
//   r.vals.avgSkus     → avgSkus
//   r.vals.caseQuantity→ caseQty    (UI field name differs from API field name)

function adaptApiRows(rows: ASNLocationDetailsRow[]): MockLocationRow[] {
  const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, "");

  const getGrainValue = (
    grainVals: ASNLocationDetailsRow["grainVals"] | Record<string, unknown> | undefined,
    grainKey: AsnLocationGrainKey,
  ): string => {
    if (!grainVals || typeof grainVals !== "object") return "";

    const rawMap = grainVals as Record<string, unknown>;
    const direct = rawMap[grainKey];
    const target = normalizeKey(grainKey);
    const resolved =
      direct ??
      Object.entries(rawMap).find(([rawKey]) => normalizeKey(rawKey) === target)?.[1];

    if (resolved == null) return "";
    if (typeof resolved === "string") return resolved.trim();
    if (typeof resolved === "number") return Number.isFinite(resolved) ? String(resolved) : "";
    if (typeof resolved === "boolean") return resolved ? "true" : "false";
    return String(resolved).trim();
  };

  return rows.map((r) => ({
    id: r.key,
    region:       getGrainValue(r.grainVals, "region"),
    market:       getGrainValue(r.grainVals, "market"),
    location:     getGrainValue(r.grainVals, "location"),
    locationType: getGrainValue(r.grainVals, "locationType"),
    year:         getGrainValue(r.grainVals, "year"),
    period:       getGrainValue(r.grainVals, "period"),
    week:         getGrainValue(r.grainVals, "week"),
    // Metric fields: coerce null/undefined to 0 at the API boundary.
    // The TypeScript type says `number` but real payloads can carry null for
    // fields like pyPct or pctNonAsn when the backend has no prior-year data or
    // when totalLoads is 0. Coercing to 0 here keeps the UI row shape correct;
    // formatMetric's own guard renders '—' for any remaining non-finite values.
    nonAsnLoads: r.vals.nonAsnLoads  ?? 0,
    totalLoads:  r.vals.totalLoads   ?? 0,
    pctNonAsn:   r.vals.pctNonAsn    ?? 0,
    pyPct:       r.vals.pyPct        ?? 0,
    avgSkus:     r.vals.avgSkus      ?? 0,
    caseQty:     r.vals.caseQuantity ?? 0,
    // linkedLocations is not present on API rows; omitted (field is now optional)
  }));
}

// ─── Component ────────────────────────────────────────────────────────────────

const LocationSummaryV2Table: React.FC<Props> = ({
  apiRows,
  perspective,
  effectiveGrainKeys,
  grandTotal,
  serverPagination,
  loading,
  error,
  sortBy,
  sortDirection,
  searchInput,
  selectedKey,
  onSelect,
  onSort,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onExport,
  isExporting = false,
}) => {
  const { width: windowWidth } = useWindowDimensions();
  const isMobileToolbar = windowWidth < MOBILE_TOOLBAR_BREAKPOINT;
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>({
    region: true,
    market: false,
    location: false,
    locationType: false,
    year: false,
    period: false,
    week: false,
  });
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const columnMenuAnchorRef = useRef<View | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [tableViewportWidth, setTableViewportWidth] = useState(0);

  // ─── Column visibility ────────────────────────────────────────────────────
  // Show the grain columns where the user has not explicitly hidden them.
  // The effectiveGrainKeys guard is intentionally NOT applied here — it only
  // describes which grains the API returned data for on this page, not which
  // columns the user wants to see. Applying it as a column gate was the root
  // cause of Period and Week toggles having no visible effect: those keys are
  // absent from effectiveGrainKeys (they are not in DEFAULT_GRAIN_SELECTIONS)
  // so the filter discarded them even after visible[grain] was set to true.
  const grains = ALL_GRAINS.filter(
    (grain) => visible[grain] !== false
  ) as MockGrain[];

  const metrics = MOCK_METRICS.filter(
    (metric) => visible[metric.key] !== false
  );

  // Export always includes all grains and all metrics regardless of which
  // columns are currently toggled visible in the table — the exported document
  // shows the full dataset.
  const allGrainsForExport = ALL_GRAINS as unknown as MockGrain[];
  const allMetricExportColumns = buildVisibleMetricExportColumns(MOCK_METRICS);

  const columnWidths = useMemo(
    () =>
      computeColumnWidths(grains.length, metrics.length, tableViewportWidth),
    [grains.length, metrics.length, tableViewportWidth]
  );

  // ─── Adaptation ───────────────────────────────────────────────────────────
  const adaptedRows = useMemo(() => adaptApiRows(apiRows), [apiRows]);

  // ─── Hierarchy (client-side, over the current page of adapted rows) ───────
  //
  // The hierarchy builders (buildRegionHierarchyRows / buildMarketHierarchyRows)
  // group *location-level* source rows into region/market/location expand trees.
  // They must only run when the API actually returned location-level rows —
  // i.e. when "location" is among effectiveGrainKeys. When the backend has
  // already pre-aggregated at region or market level (effectiveGrainKeys does
  // not include "location"), each adapted row IS the rollup row; running the
  // builder on them would re-group them and produce duplicated / echoed values
  // in the rendered cells (the original bug: every row showing the same region
  // repeated as region + market + location values).
  //
  // Rule: hierarchy builder is only invoked when the data is location-granular.
  // In all other cases the rows are already at the correct grain and are
  // rendered flat, one per API row.
  const sourceIsLocationLevel = effectiveGrainKeys.includes("location" as AsnLocationGrainKey);

  const hierarchyMode = useMemo(
    () => {
      if (!sourceIsLocationLevel) return "flat" as const;
      return deriveActiveHierarchyMode(grains);
    },
    [grains, sourceIsLocationLevel]
  );

  const hierarchyRows = useMemo(() => {
    if (hierarchyMode === "flat") return adaptedRows;
    if (hierarchyMode === "region") return buildRegionHierarchyRows(adaptedRows);
    if (hierarchyMode === "market") return buildMarketHierarchyRows(adaptedRows);
    return adaptedRows;
  }, [adaptedRows, hierarchyMode]);

  // ─── Search (client-side filter over the current page) ───────────────────
  const searchContext = useMemo(
    () => buildSearchContext(hierarchyRows, adaptedRows, searchInput, hierarchyMode),
    [hierarchyRows, adaptedRows, searchInput, hierarchyMode]
  );

  const autoExpandKeys = useMemo(
    () => determineAutoExpandKeys(hierarchyRows, searchContext, hierarchyMode),
    [hierarchyRows, searchContext, hierarchyMode]
  );

  const effectiveExpandedGroups = useMemo(() => {
    const merged = new Set(expandedGroups);
    autoExpandKeys.forEach((key) => merged.add(key));
    return merged;
  }, [expandedGroups, autoExpandKeys]);

  const displayRows = useMemo(
    () =>
      filterForVisibility(
        hierarchyRows,
        hierarchyMode,
        effectiveExpandedGroups,
        searchContext
      ),
    [hierarchyRows, hierarchyMode, effectiveExpandedGroups, searchContext]
  );

  // ─── Pagination ───────────────────────────────────────────────────────────
  // Prefer server-side pagination metadata when available.
  const totalRows = serverPagination?.totalRowCount ?? displayRows.length;
  const currentPage = serverPagination?.pageIndex ?? 0;
  const currentPageSize = serverPagination?.pageSize ?? 25;
  const pages = serverPagination
    ? Math.max(1, serverPagination.totalPages)
    : Math.max(1, Math.ceil(displayRows.length / currentPageSize));

  // When server pagination is active the backend already returns only one page
  // of rows, so pageRows = all displayRows. Client-side slicing is only used
  // as a fallback when serverPagination is null (e.g. during initial loading).
  const pageRows = serverPagination
    ? displayRows
    : displayRows.slice(
        currentPage * currentPageSize,
        (currentPage + 1) * currentPageSize
      );

  // ─── Grand total row ──────────────────────────────────────────────────────
  // When the backend provides a grandTotal, map it to the metric Record shape
  // the TotalRow sub-component expects. Fall back to a client-side sum over
  // displayRows when the server total is not yet available.
  const totals = useMemo<Record<string, number>>(() => {
    if (grandTotal) {
      return {
        nonAsnLoads: grandTotal.nonAsnLoads,
        totalLoads: grandTotal.totalLoads,
        pctNonAsn: grandTotal.pctNonAsn,
        pyPct: 0,
        avgSkus: grandTotal.avgSkus,
        caseQty: grandTotal.caseQuantity,
      };
    }
    return metrics.reduce((acc, metric) => {
      acc[metric.key] = displayRows.reduce(
        (sum, row) => sum + metricValue(row, metric.key),
        0
      );
      return acc;
    }, {} as Record<string, number>);
  }, [grandTotal, displayRows, metrics]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const toggleGroup = (row: MockLocationRow) => {
    const key = row.id;
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedGroups(newExpanded);
  };

  const handleToggleColumn = useCallback((columnKey: string) => {
    setVisible((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  // Sort is server-driven — delegate to the parent's handler.
  const handleSort = useCallback(
    (key: string) => { onSort(key); },
    [onSort]
  );

  // Bridge: sub-components call onSelect(MockLocationRow); we find the matching
  // ASNLocationDetailsRow by its key and forward it to the parent. Region/market
  // hierarchy rows have no matching flat apiRow (their id is a synthetic group
  // key) — build a scoped anchor from the row's own region/market so the
  // companion table can look up locations under that region/market instead.
  const handleRowSelect = useCallback(
    (mockRow: MockLocationRow) => {
      const apiRow = apiRows.find((r) => r.key === mockRow.id);
      if (apiRow) {
        onSelect(apiRow);
        return;
      }
      if (mockRow.groupType === "region" || mockRow.groupType === "market") {
        const grainVals: Partial<Record<AsnLocationGrainKey, string>> = {
          region: mockRow.region,
        };
        if (mockRow.groupType === "market") {
          grainVals.market = mockRow.market;
        }
        onSelect({
          key: mockRow.id,
          grainVals,
          vals: { nonAsnLoads: 0, totalLoads: 0, pctNonAsn: 0, avgSkus: 0, caseQuantity: 0, pyPct: 0 },
          memberCount: 0,
        });
      }
    },
    [apiRows, onSelect]
  );

  // ─── Subtitle ─────────────────────────────────────────────────────────────
  const grainLevelText =
    grains.length === 0
      ? "No grains selected"
      : grains.map(getGrainLabel).join(" › ") + " level";

  const getSubtitleText = () => {
    if (grains.length === 0) {
      return "Select at least one grain column using the Columns button.";
    }
    const otherPerspective =
      perspective === "shipping" ? "receiving" : "shipping";
    const arrowTip =
      hierarchyMode === "market" ? `Click > to expand regions. ` : "";
    return `Showing: ${grainLevelText}. ${arrowTip}Click any row to see ${otherPerspective} locations below.`;
  };

  // Zero rows on the current page, once loading/error are ruled out, get one
  // of two messages depending on whether a search is driving the result —
  // `searchContext.hasQuery` already applies the app's existing whitespace-safe
  // (`.trim().length > 0`) normalization, reused here rather than duplicated.
  const hasNoRows = !loading && !error && pageRows.length === 0;
  const isSearchEmptyResult = hasNoRows && searchContext.hasQuery;
  const isGenericEmptyResult = hasNoRows && !searchContext.hasQuery;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      {isMobileToolbar ? (
        /* ── Mobile header: title/subtitle stacked, Columns button on its own row ── */
        <View style={[styles.header, tableHeaderMobileStyles.headerMobile]}>
          <View style={tableHeaderMobileStyles.titleBlockMobile}>
            <Text style={styles.headerTitle}>
              {perspective === "shipping" ? "Shipping Locations" : "Receiving Locations"}
            </Text>
            <Text style={styles.headerSubtitle}>{getSubtitleText()}</Text>
          </View>
          {/* Columns button right-aligned, on its own row */}
          <View style={tableHeaderMobileStyles.actionRowMobile}>
            <LocationSummaryToolbar
              query={searchInput}
              isSearchFocused={isSearchFocused}
              isMobileToolbar={isMobileToolbar}
              columnsAnchorRef={columnMenuAnchorRef}
              onSearchChange={onSearchChange}
                onSearchFocus={() => setIsSearchFocused(true)}
                onSearchBlur={() => setIsSearchFocused(false)}
                onToggleColumns={() => setColumnMenuOpen((prev) => !prev)}
                onExport={() => onExport(allMetricExportColumns, allGrainsForExport)}
                isExporting={isExporting}
                disableExport={hasNoRows}
                disabled={loading}
                columnsDropdown={
                  <LocationSummaryColumnsDropdown
                    isOpen={columnMenuOpen}
                    anchorRef={columnMenuAnchorRef}
                    visible={visible}
                    onToggleColumn={handleToggleColumn}
                    onRequestClose={() => setColumnMenuOpen(false)}
                  />
                }
            />
          </View>
        </View>
      ) : (
        /* ── Web/tablet header: unchanged side-by-side layout ── */
        <View style={styles.header}>
          <View style={styles.headerTitleSection}>
            <Text style={styles.headerTitle}>
              {perspective === "shipping"
                ? "Shipping Locations"
                : "Receiving Locations"}
            </Text>
            <Text style={styles.headerSubtitle}>{getSubtitleText()}</Text>
          </View>

          <LocationSummaryToolbar
            query={searchInput}
            isSearchFocused={isSearchFocused}
            isMobileToolbar={isMobileToolbar}
            columnsAnchorRef={columnMenuAnchorRef}
            onSearchChange={onSearchChange}
            onSearchFocus={() => setIsSearchFocused(true)}
            onSearchBlur={() => setIsSearchFocused(false)}
            onToggleColumns={() => setColumnMenuOpen((prev) => !prev)}
            onExport={() => onExport(allMetricExportColumns, allGrainsForExport)}
            isExporting={isExporting}
            disableExport={hasNoRows}
            disabled={loading}
            columnsDropdown={
              <LocationSummaryColumnsDropdown
                isOpen={columnMenuOpen}
                anchorRef={columnMenuAnchorRef}
                visible={visible}
                onToggleColumn={handleToggleColumn}
                onRequestClose={() => setColumnMenuOpen(false)}
              />
            }
          />
        </View>
      )}

      <View
        style={{ width: "100%", zIndex: -9 }}
        onLayout={(event) => {
          setTableViewportWidth(event.nativeEvent.layout.width);
        }}
      >
        {!loading && error && (
          <Text style={[styles.headerSubtitle, { color: "#C0392B" }]}>
            {error}
          </Text>
        )}

        {metrics.length === 0 ? (
          <View style={styles.emptyStateContainer}>
            <Text style={styles.emptyStateText}>
              Select at least one metric using the Columns button.
            </Text>
          </View>
        ) : (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator
            style={[styles.tableWrapper, { width: "100%" }]}
            testID="asn-v2-table"
          >
            <View style={[styles.table, { width: columnWidths.table }]}>
              <LocationSummaryTableHeader
                grains={grains}
                metrics={metrics}
                sort={{ key: sortBy, desc: sortDirection === "desc" }}
                columnWidths={columnWidths}
                onSort={handleSort}
              />
              {loading ? (
                <TableSkeleton rows={Math.min(currentPageSize, 10)} />
              ) : (
                <>
                  <LocationSummaryTotalRow
                    grains={grains}
                    metrics={metrics}
                    totals={totals}
                    columnWidths={columnWidths}
                  />
                  {isSearchEmptyResult ? (
                    <View
                      style={styles.emptyStateContainer}
                      testID="asn-v2-search-empty-state"
                    >
                      <Text style={styles.emptyStateText}>
                        No rows match the current search.
                      </Text>
                    </View>
                  ) : isGenericEmptyResult ? (
                    <View
                      style={styles.emptyStateContainer}
                      testID="asn-v2-empty-state"
                    >
                      <Text style={styles.emptyStateText}>
                        {`No ${perspective} location data available for the selected filters`}
                      </Text>
                    </View>
                  ) : (
                    pageRows.map((row) => (
                      <LocationSummaryTableRow
                        key={row.id}
                        row={row}
                        grains={grains}
                        metrics={metrics}
                        hierarchyMode={hierarchyMode}
                        effectiveExpandedGroups={effectiveExpandedGroups}
                        selectedId={selectedKey}
                        columnWidths={columnWidths}
                        onToggleGroup={toggleGroup}
                        onSelect={handleRowSelect}
                      />
                    ))
                  )}
                </>
              )}
            </View>
          </ScrollView>
        )}

        {metrics.length > 0 && (
          <LocationSummaryPagination
            totalRows={totalRows}
            pageSize={currentPageSize}
            page={currentPage}
            pages={pages}
            onPageSizeChange={(size) => { onPageSizeChange(size); }}
            onPrev={() => onPageChange(currentPage - 1)}
            onNext={() => onPageChange(currentPage + 1)}
            onFirst={() => onPageChange(0)}
            onLast={() => onPageChange(pages - 1)}
            disabled={loading}
          />
        )}
      </View>
    </View>
  );
};

// ─── Mobile-only header styles ────────────────────────────────────────────────
const tableHeaderMobileStyles = StyleSheet.create({
  // Stack the header children vertically and stretch to full width
  headerMobile: {
    flexDirection: "column",
    alignItems: "stretch",
    width: "100%",
  },
  // Title + subtitle block occupies full width
  titleBlockMobile: {
    width: "100%",
    marginBottom: 8,
  },
  // Action row: Columns button right-aligned, no fixed left offset
  actionRowMobile: {
    width: "100%",
    alignItems: "flex-end",
  },
});

export default LocationSummaryV2Table;
